import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import GlassCard from "../components/GlassCard";
import PrimaryButton, { GlassButton } from "../components/PrimaryButton";
import BottomNav from "../components/BottomNav";
import PageShell from "../components/PageShell";
import { Mic, MessageSquareHeart, ListChecks, AlertCircle, TrendingUp } from "lucide-react";

const Dashboard = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/sessions").then((r) => setSessions(r.data || [])).catch(() => {});
    api.get("/stats/usage").then((r) => setStats(r.data)).catch(() => {});
  }, [user]);

  if (!user) return null;

  const firstName =
    (user?.name || "there").trim().split(/\s+/).filter(p => !/^(dr\.?|mr\.?|ms\.?|mrs\.?|prof\.?)$/i.test(p))[0]
    || user?.name || "there";

  const subline = {
    therapist: "Ready to capture today's session?",
    coach: "Ready to capture today's session?",
    client: "Ready to record and reflect on your session?",
  }[user.role] || "Ready to capture today's session?";

  const recent = sessions.slice(0, 3);

  return (
    <PageShell showSettings>
      <div className="pt-16 pb-32 max-w-2xl mx-auto flex flex-col gap-8 px-2">
        {user.subscription_status === "past_due" && (
          <button
            onClick={() => nav("/settings")}
            className="text-left"
            data-testid="past-due-banner"
          >
            <GlassCard className="!bg-[#B85C5C]/10 border-[#B85C5C]/30 hover:!bg-[#B85C5C]/15 transition-colors">
              <div className="flex items-start gap-3">
                <AlertCircle size={22} className="text-[#B85C5C] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[#2A3B32]">Last payment failed</p>
                  <p className="mt-1 text-sm text-[#4A5D52]">Update your payment method to keep access. Tap to open Settings.</p>
                </div>
              </div>
            </GlassCard>
          </button>
        )}
        {user.subscription_status === "canceling" && user.subscription_ends_at && (
          <GlassCard className="!bg-[#E0B96A]/10 border-[#E0B96A]/30" data-testid="canceling-banner">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-[#8B6E1F] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#4A5D52]">
                Your subscription is canceling. You&apos;ll keep access until{" "}
                <span className="font-semibold text-[#2A3B32]">
                  {new Date(user.subscription_ends_at).toLocaleDateString()}
                </span>.
              </p>
            </div>
          </GlassCard>
        )}
        <div className="text-center">
          <p className="text-xs font-semibold tracking-wider text-[#8BA38E] uppercase">
            {user.role === "client" ? "Personal Sessions" : "Session Notes"}
          </p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-bold text-[#2A3B32]">
            Hello, {firstName}
          </h1>
          <p className="mt-3 text-base text-[#4A5D52]">{subline}</p>
        </div>

        <div className="flex flex-col gap-3">
          <PrimaryButton onClick={() => nav("/new-session")} data-testid="start-session-btn">
            <Mic size={20} className="mr-2" /> Start New Session
          </PrimaryButton>
          {user.role === "client" && (
            <GlassButton onClick={() => nav("/feedback")} data-testid="send-feedback-btn">
              <MessageSquareHeart size={18} className="mr-2" /> Send Feedback to Practitioner
            </GlassButton>
          )}
        </div>

        {stats && (stats.sessions_total > 0 || stats.journal_this_month > 0 || stats.feedback_exchanged_this_month > 0) && (
          <GlassCard data-testid="usage-stats-card">
            <div className="flex items-center gap-2">
              <TrendingUp size={16} className="text-[#8BA38E]" />
              <h3 className="text-xs font-semibold text-[#8BA38E] uppercase tracking-wider">This month</h3>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="text-center" data-testid="stat-sessions-month">
                <p className="text-2xl font-bold text-[#2A3B32]">{stats.sessions_this_month}</p>
                <p className="text-[11px] text-[#8A9C91] mt-1 leading-tight">Sessions recorded</p>
              </div>
              <div className="text-center" data-testid="stat-journal-month">
                <p className="text-2xl font-bold text-[#2A3B32]">{stats.journal_this_month}</p>
                <p className="text-[11px] text-[#8A9C91] mt-1 leading-tight">Journal entries</p>
              </div>
              <div className="text-center" data-testid="stat-feedback-month">
                <p className="text-2xl font-bold text-[#2A3B32]">{stats.feedback_exchanged_this_month}</p>
                <p className="text-[11px] text-[#8A9C91] mt-1 leading-tight">Feedback exchanged</p>
              </div>
            </div>
            {stats.sessions_total > 0 && (
              <p className="mt-4 text-center text-xs text-[#8A9C91]">
                {stats.sessions_total} total session{stats.sessions_total !== 1 ? "s" : ""} on Well
              </p>
            )}
          </GlassCard>
        )}

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between px-2">
            <h2 className="text-lg font-semibold text-[#2A3B32]">Recent sessions</h2>
            {sessions.length > 3 && (
              <button
                onClick={() => nav("/sessions")}
                className="text-xs font-medium text-[#8BA38E] hover:text-[#6A7F6C]"
                data-testid="see-all-sessions"
              >
                See all
              </button>
            )}
          </div>
          {recent.length === 0 ? (
            <GlassCard className="text-center" data-testid="no-sessions">
              <ListChecks size={28} className="mx-auto text-[#8BA38E]/60 mb-3" strokeWidth={1.5} />
              <p className="text-[#4A5D52]">Your session notes will appear here.</p>
              <p className="mt-1 text-xs text-[#8A9C91]">Tap Start New Session above to record or write your first one.</p>
            </GlassCard>
          ) : (
            recent.map((s) => {
              const isDraft = s.status === "draft";
              const preview = s.source_type === "ai_listen"
                ? (s.sections?.session_overview || "")
                : (s.notes_content || "");
              return (
                <button
                  key={s.session_id}
                  onClick={() => nav(isDraft ? `/review/${s.session_id}` : `/results/${s.session_id}`)}
                  className="text-left"
                  data-testid={`session-card-${s.session_id}`}
                >
                  <GlassCard className="hover:bg-white/55 transition-colors">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-semibold text-[#2A3B32]">{s.client_name}</p>
                      <div className="flex items-center gap-2">
                        {isDraft ? (
                          <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E0B96A]/25 text-[#8B6E1F] border border-[#E0B96A]/40">Draft</span>
                        ) : (
                          <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#8BA38E]/20 text-[#6A7F6C] border border-[#8BA38E]/30">Sent</span>
                        )}
                        <p className="text-xs text-[#8A9C91]">{new Date(s.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-[#4A5D52] line-clamp-2">{preview.slice(0, 140)}{preview.length > 140 ? "…" : ""}</p>
                  </GlassCard>
                </button>
              );
            })
          )}
        </div>

        {user.role !== "client" && false && feedbackCount > 0 && (
          <button onClick={() => nav("/feedback")} className="text-left" data-testid="feedback-summary-card">
            <GlassCard className="hover:bg-white/55 transition-colors">
              <div className="flex items-center gap-3">
                <MessageSquareHeart size={24} className="text-[#8BA38E]" strokeWidth={1.5} />
                <div className="flex-1">
                  <p className="font-semibold text-[#2A3B32]">{feedbackCount} client feedback note{feedbackCount !== 1 ? "s" : ""}</p>
                  <p className="text-xs text-[#8A9C91]">Tap to review and attach to your next session.</p>
                </div>
              </div>
            </GlassCard>
          </button>
        )}
      </div>
      <BottomNav />
    </PageShell>
  );
};

export default Dashboard;
